import React, { useEffect, useState } from 'react';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import TableFunction from '../../../../../components/table/TableFunction';
import { useTranslation } from 'react-i18next';
import CustomImageTable from '../../../../../components/CustomImageTable';
import { PROTOCOL_DATA, READER_DATA, READER_MODE_DATA, READER_NUMBER_DATA } from '../../Readers/Model/Models';
import { GetHidRecordDetails } from "../../../../../reduxToolkit/HID/HidApi";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../../../Apis/Tables";
import { getDeviceAccessTypeById, getIconClassesContainer } from "../../../Devices/Model/Model";
import {EMPTY_VALUES, errorMsg, getValueByKey} from "../../../../../Apis/functions";
import { CheckIconsOptions, CrossIconsOptions } from "../../../../../components/buttons/IconButtons";
import { toast } from "react-toastify";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * ReaderTableApp displays and manages the Readers table for a specific controller/SIO.
 * Supports selection, deletion, and modal creation for new readers.
 *
 * @param {Array} defaultSioList - Optional default list of SIOs with their readers to initialize the table.
 *
 * @returns {Object} - Table headers, body, selection state, modal controls, and CRUD functions.
 */
const ReaderTableApp = ({ defaultSioList }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();

    const [tableId, setTableId] = useState("tbl_rta_table");
    const [deleteReaderShow, setDeleteReaderShow] = useState(false);
    const [selectedList, setSelectedList] = useState([]);
    const [showCreateReaderModal, setShowCreateReaderModal] = useState(false);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [modified, setModified] = useState(false);
    const [results, setResults] = useState({ content: [], totalElements: 0 });

    /**
     * Initialize table data with defaultSioList if provided
     */
    useEffect(() => {
        if (defaultSioList?.length > 0) {
            const aux = [];
            defaultSioList.forEach((row) => {
                row.readers.forEach((e) => aux.push(READER_DATA.set(e, row.name)));
            });
            setResults({ content: aux, totalElements: aux.length });
        }
    }, [defaultSioList]);

    /**
     * Builds table headers (thead)
     * @returns {Array<JSX.Element>}
     */
    const getThead = () => [
        <CustomTh tableId={tableId} className='first_head'>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('reader_number')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('access_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('key_pad')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('communication_protocol')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('reader_mode')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('keep_open')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('sio')}</CustomTh>
    ];

    /**
     * Builds table body (tbody)
     * @returns {Object} - { body: JSX.Elements, data: Array }
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };

        const iconClassContainer = getIconClassesContainer(results.content);
        const body = results.content.map((item) => {
            const deviceAccessType = getDeviceAccessTypeById(item?.deviceAccessTypeId);

            return (
                <>
                    <CustomTd tableId={tableId} className='first-column'>{item?.name}</CustomTd>
                    <CustomTd tableId={tableId}>{t(getValueByKey(item?.readerNo, 'name', READER_NUMBER_DATA))}</CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomImageTable
                            children={<span className="text-ellipsis text-left">{t(deviceAccessType?.trans)}</span>}
                            tooltip={t(deviceAccessType?.trans)}
                            containerClass={"d-flex"}
                            icon={<div className={`${iconClassContainer} mr-2 ${deviceAccessType.color}`}><deviceAccessType.Icon/></div>}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomImageTable image={item?.hasKeyPad ? CheckIconsOptions : CrossIconsOptions} />
                    </CustomTd>
                    <CustomTd tableId={tableId}>{getValueByKey(item?.communicationProtocol, 'name', PROTOCOL_DATA)}</CustomTd>
                    <CustomTd tableId={tableId}>{t(getValueByKey(item?.readerMode, 'name', READER_MODE_DATA)) || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.keepOpen || EMPTY_VALUES.DEFAULT}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.hidSioName}</CustomTd>
                </>
            );
        });

        return { body, data: results.content };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        dispatch(GetHidRecordDetails({ id })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                const aux = [];
                data?.sios.forEach((row) => {
                    row.readers.forEach((e) => aux.push(READER_DATA.set(e, row.name)));
                });
                setResults({ content: aux, totalElements: aux.length });
            }
        });
    }

    const { thead, tbody, handleCheckboxChange, handleDeleteAll } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getTbody,
        getThead,
        setIsAllChecked,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Delete selected readers and refresh table
     */
    const deleteSelectedReader = () => {
        const body = selectedList;
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: TABLES.HID_READER, body })).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    setSelectedList([]);
                    setIsAllChecked(false);
                    setModified(true);
                }else errorMsg(code);
            });
        }
    };

    return {
        t,
        id,
        thead,
        tbody,
        tableId,
        selectedList,
        showCreateReaderModal,
        setShowCreateReaderModal,
        deleteReaderShow,
        setDeleteReaderShow,
        isAllChecked,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelectedReader,
    };
};

export default ReaderTableApp;
